import * as React from 'react';
import { ChangeEvent, memo, useEffect, useMemo } from 'react';
import { InputAdornment, IconButton } from '@mui/material';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { Form, useTranslate, useListFilterContext } from 'ra-core';
import { TextInput, TextInputProps, useRedirect, useRefresh } from 'react-admin';
import { FilterLiveSearch } from 'react-admin';
import { toInteger } from 'lodash';
import { useLocation } from 'react-router-dom';

/**
 * Form and search input for doing a full-text search filter.
 *
 * Triggers a search on change (with debounce).
 *
 * @example
 *
 * const FilterPanel = () => (
 *     <Card>
 *         <CardContent>
 *             <FilterLiveSearch source="title" />
 *         </CardContent>
 *     </Card>
 * );
 */
export const LiveSearchBox = memo((props: LiveSearchBoxProps) => {
  const { filterValues, setFilters } = useListFilterContext();
  const [inputValue, setInputValue] = React.useState('');
  const translate = useTranslate();
  const redirect = useRedirect();
  const location = useLocation();
  const refresh = useRefresh();


  const {
    source = 'q',
    variant,
    label = translate('ra.action.search'),
    ...rest
  } = props;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {

    if (event && event.target) {
      const newValue = event.target.value;
      setInputValue(newValue);

      if(newValue == ''){
        clearFilters();
      }
    }
    else{
        setInputValue('');
        clearFilters();
    }
  };

  const clearFilters = () => {
    const { 'combined_unaccent@ilike': _, ...filters } = filterValues;
    setFilters(filters, null);
    setInputValue('');
  }

  const handleClick = (event: any) => {
    if (event.target || inputValue) {
      setFilters(
        { ...filterValues, 'combined_unaccent@ilike': inputValue },
        null
      );
    } else {
        clearFilters();
    }
  };

  useEffect(() => {
    if (location.pathname === '/funerals' && location.search === '') {

        setInputValue(''); // Reset inputValue when on /home
        clearFilters();
    }
    }, [location]);

  const handleKey = (event: any) => {
    if (event.keyCode == 13) {
      if (!isNaN(parseFloat(inputValue))) {
        redirect(`/funerals/${event.target.value}/show`);
      } else {
        handleClick(event);
      }
      // put the login here
    }
  };

//   const initialValues = useMemo(
//     () => ({
//       [source]: '',
//     }),
//     [filterValues, source]
//   );

  const onSubmit = () => undefined;

  return (
    <Form onSubmit={onSubmit}>

      <TextInput
        resettable
        helperText={false}
        source={source}
        defaultValue={inputValue}
        value={inputValue}
        InputProps={{
          style: { paddingLeft: 0 },
          startAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClick}>
                {' '}
                {/* Wrap the icon in IconButton and provide an onClick event */}
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onChange={handleChange}
        onKeyDown={handleKey}
        size="small"
        {...(variant === 'outlined'
          ? { variant: 'outlined', label }
          : {
              placeholder: label,
              label: false,
              hiddenLabel: true,
            })}
        {...rest as any}
      />
    </Form>
  );
});

export interface LiveSearchBoxProps extends Omit<TextInputProps, 'source'> {
  source?: string;
  sx?: any;
  label?: string;
  fullWidth?: boolean;
  variant?: 'filled' | 'outlined';
}
